/**
 * Admin page for managing quarantine items
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Loader2,
  FileQuestion,
  Merge,
  ArrowRight,
  Package,
  Search,
  MapPin,
} from 'lucide-react'
import { cn } from '../../lib/utils'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8899'

interface QuarantineItem {
  id: number
  quarantine_type: string
  status: string
  source_job_id: string | null
  source_file: string | null
  source_line: number | null
  entity_type: string | null
  entity_key: string | null
  entity_name: string | null
  issue_code: string
  issue_message: string
  issue_details: Record<string, unknown> | null
  raw_data: Record<string, unknown>
  parsed_data: Record<string, unknown> | null
  suggested_action: string | null
  match_candidates: Record<string, unknown> | null
  priority: number
  batch_id: string | null
  created_at: string | null
  resolved_at: string | null
  resolved_by: string | null
  resolution_action: string | null
  resolution_notes: string | null
}

interface QuarantineListResponse {
  total: number
  pending: number
  items: QuarantineItem[]
}

interface QuarantineBatch {
  id: number
  batch_id: string
  name: string
  source_job_id: string | null
  total_items: number
  pending_items: number
  approved_items: number
  rejected_items: number
  is_complete: boolean
  created_at: string | null
  completed_at: string | null
}

interface QuarantineStats {
  total_items: number
  pending_items: number
  approved_items: number
  rejected_items: number
  by_type: Record<string, number>
  by_entity_type: Record<string, number>
  recent_batches: QuarantineBatch[]
}

interface EntitySearchResult {
  id: number
  key: string
  name: string
  label: string  // Full display name with type
  entity_type: string
}

// Status badge colors and icons
const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  pending: { color: 'bg-yellow-100 text-yellow-700', icon: <Clock className="w-4 h-4" />, label: 'Ausstehend' },
  approved: { color: 'bg-green-100 text-green-700', icon: <CheckCircle2 className="w-4 h-4" />, label: 'Genehmigt' },
  rejected: { color: 'bg-red-100 text-red-700', icon: <XCircle className="w-4 h-4" />, label: 'Abgelehnt' },
  merged: { color: 'bg-blue-100 text-blue-700', icon: <Merge className="w-4 h-4" />, label: 'Zusammengeführt' },
  deferred: { color: 'bg-gray-100 text-gray-700', icon: <Clock className="w-4 h-4" />, label: 'Zurückgestellt' },
}

// Quarantine type labels
const typeLabels: Record<string, string> = {
  unmatched_entity: 'Unzugeordnete Entität',
  unmatched_region: 'Unzugeordnete Region',
  new_indicator: 'Neuer Indikator',
  validation_error: 'Validierungsfehler',
  duplicate: 'Duplikat',
  data_conflict: 'Datenkonflikt',
  unknown_code: 'Unbekannter Code',
}

// Entity type labels
const entityTypeLabels: Record<string, string> = {
  state: 'Bundesland',
  government_district: 'Regierungsbezirk',
  county: 'Kreis',
  municipal_association: 'Gemeindeverband',
  municipality: 'Gemeinde',
  // Classification types
  region_types: 'Regionstyp',
  county_types: 'Kreistyp',
  differentiated_region_types: 'Diff. Regionstyp',
  spatial_planning_regions: 'Raumordnungsregion',
  labor_market_regions: 'Arbeitsmarktregion',
  planning_regions: 'Planungsregion',
  urbanization_areas: 'Verstädterungsgebiet',
  travel_regions: 'Reisegebiet',
  municipality_type_classifications: 'Gemeindetyp',
  urban_rural_types: 'Stadt-/Landtyp',
  centrality_types: 'Zentralitätstyp',
}

// Issue codes that are classification-related
const CLASSIFICATION_ISSUE_CODES = ['NEW_CLASSIFICATION_CODE', 'MISSING_CLASSIFICATION_CODE']

// Issue codes that need entity assignment
const ENTITY_ASSIGNMENT_ISSUE_CODES = ['ENTITY_NOT_FOUND', 'DUPLICATE_ENTITY_MATCH']

function StatusBadge({ status }: { status: string }) {
  const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-700', icon: null, label: status }
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium', config.color)}>
      {config.icon}
      {config.label}
    </span>
  )
}

function PriorityBadge({ priority }: { priority: number }) {
  const colors = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-yellow-100 text-yellow-700',
    low: 'bg-gray-100 text-gray-700',
  }
  const level = priority >= 8 ? 'high' : priority >= 4 ? 'medium' : 'low'
  const label = priority >= 8 ? 'Hoch' : priority >= 4 ? 'Mittel' : 'Niedrig'

  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', colors[level])}>
      {label}
    </span>
  )
}

function QuarantineItemRow({
  item,
  onResolve,
  onCreateClassification,
  onIgnoreMissing,
  onDeleteClassification,
  onAssignEntity,
  isResolving,
}: {
  item: QuarantineItem
  onResolve: (id: number, action: string, notes?: string) => void
  onCreateClassification: (id: number, name: string, bbsrCode?: string, description?: string) => void
  onIgnoreMissing: (id: number) => void
  onDeleteClassification: (id: number) => void
  onAssignEntity: (id: number, entityId: number, notes?: string, overwrite?: boolean) => void
  isResolving: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const [notes, setNotes] = useState('')
  const [classificationName, setClassificationName] = useState('')
  const [classificationBbsrCode, setClassificationBbsrCode] = useState('')
  const [classificationDescription, setClassificationDescription] = useState('')
  const [entitySearchQuery, setEntitySearchQuery] = useState('')
  const [selectedEntity, setSelectedEntity] = useState<EntitySearchResult | null>(null)
  const [overwriteExisting, setOverwriteExisting] = useState(false)
  const isPending = item.status === 'pending'
  const isClassificationItem = CLASSIFICATION_ISSUE_CODES.includes(item.issue_code)
  const isNewClassification = item.issue_code === 'NEW_CLASSIFICATION_CODE'
  const isMissingClassification = item.issue_code === 'MISSING_CLASSIFICATION_CODE'
  const isEntityAssignmentItem = ENTITY_ASSIGNMENT_ISSUE_CODES.includes(item.issue_code)

  // Entity search query - only run when no entity is selected
  const { data: searchResults, isLoading: isSearching } = useQuery<EntitySearchResult[]>({
    queryKey: ['entity-search', entitySearchQuery, item.entity_type],
    queryFn: async () => {
      if (!entitySearchQuery || entitySearchQuery.length < 2) return []
      const params = new URLSearchParams({
        q: entitySearchQuery,
        entity_type: item.entity_type || 'county',
        limit: '20',
      })
      const res = await fetch(`${API_BASE}/admin/quarantine/entities/search?${params}`)
      if (!res.ok) throw new Error('Search failed')
      return res.json()
    },
    enabled: entitySearchQuery.length >= 2 && !selectedEntity,
  })

  return (
    <div className="bg-white border border-gray-200 rounded-lg mb-2">
      <div
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="flex-shrink-0">
            <StatusBadge status={item.status} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium text-gray-900 truncate">
              {item.entity_name || item.entity_key || `Item #${item.id}`}
            </div>
            <div className="text-sm text-gray-500 truncate">
              {typeLabels[item.quarantine_type] || item.quarantine_type} - {item.issue_message}
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2">
            {item.entity_type && (
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                {entityTypeLabels[item.entity_type] || item.entity_type}
              </span>
            )}
            <PriorityBadge priority={item.priority} />
          </div>
        </div>
        <div className="flex items-center gap-2 ml-4">
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-4 space-y-4">
          {/* Details */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Typ:</span>
              <div className="font-medium">{typeLabels[item.quarantine_type] || item.quarantine_type}</div>
            </div>
            <div>
              <span className="text-gray-500">Entity-Typ:</span>
              <div className="font-medium">{entityTypeLabels[item.entity_type || ''] || item.entity_type || '-'}</div>
            </div>
            <div>
              <span className="text-gray-500">Schlüssel:</span>
              <div className="font-medium font-mono text-xs">{item.entity_key || '-'}</div>
            </div>
            <div>
              <span className="text-gray-500">Erstellt:</span>
              <div className="font-medium">
                {item.created_at ? new Date(item.created_at).toLocaleString('de-DE') : '-'}
              </div>
            </div>
          </div>

          {/* Issue */}
          <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
            <div className="flex items-center gap-2 text-yellow-800 font-medium text-sm">
              <AlertTriangle className="w-4 h-4" />
              Problem: {item.issue_code}
            </div>
            <div className="text-yellow-700 text-sm mt-1">
              {item.issue_message}
            </div>
            {item.issue_details && Object.keys(item.issue_details).length > 0 && (
              <pre className="text-xs text-yellow-600 mt-2 overflow-x-auto">
                {JSON.stringify(item.issue_details, null, 2)}
              </pre>
            )}
          </div>

          {/* Raw Data */}
          <div className="bg-gray-50 border border-gray-200 rounded p-3">
            <div className="text-sm font-medium text-gray-700 mb-2">Rohdaten</div>
            <pre className="text-xs text-gray-600 overflow-x-auto max-h-48">
              {JSON.stringify(item.raw_data, null, 2)}
            </pre>
          </div>

          {/* Parsed Data */}
          {item.parsed_data && Object.keys(item.parsed_data).length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <div className="text-sm font-medium text-blue-700 mb-2">Geparste Daten</div>
              <pre className="text-xs text-blue-600 overflow-x-auto max-h-48">
                {JSON.stringify(item.parsed_data, null, 2)}
              </pre>
            </div>
          )}

          {/* Match Candidates */}
          {item.match_candidates && Object.keys(item.match_candidates).length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded p-3">
              <div className="text-sm font-medium text-purple-700 mb-2">Mögliche Übereinstimmungen</div>
              <pre className="text-xs text-purple-600 overflow-x-auto max-h-48">
                {JSON.stringify(item.match_candidates, null, 2)}
              </pre>
            </div>
          )}

          {/* Suggested Action */}
          {item.suggested_action && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <ArrowRight className="w-4 h-4" />
              Vorgeschlagene Aktion: <strong>{item.suggested_action}</strong>
            </div>
          )}

          {/* Resolution Info */}
          {!isPending && (
            <div className="bg-gray-100 border border-gray-200 rounded p-3 text-sm">
              <div className="font-medium text-gray-700">Bearbeitet</div>
              <div className="text-gray-600 mt-1">
                Aktion: {item.resolution_action} | Von: {item.resolved_by || 'System'} |
                Am: {item.resolved_at ? new Date(item.resolved_at).toLocaleString('de-DE') : '-'}
              </div>
              {item.resolution_notes && (
                <div className="mt-2 text-gray-500">Notizen: {item.resolution_notes}</div>
              )}
            </div>
          )}

          {/* Actions */}
          {isPending && (
            <div className="border-t border-gray-200 pt-4">
              {/* Classification-specific actions */}
              {isClassificationItem ? (
                <div className="space-y-4">
                  {isNewClassification && (
                    <>
                      <div className="bg-blue-50 border border-blue-200 rounded p-3">
                        <div className="text-sm font-medium text-blue-800 mb-2">
                          Neue Klassifikation erstellen
                        </div>
                        <div className="text-sm text-blue-600 mb-3">
                          Code: <span className="font-mono font-bold">{item.entity_key}</span>
                        </div>
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={classificationName}
                            onChange={(e) => setClassificationName(e.target.value)}
                            className="w-full px-3 py-2 border border-blue-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Name (optional, sonst wird nur Code angezeigt)"
                          />
                          <input
                            type="text"
                            value={classificationBbsrCode}
                            onChange={(e) => setClassificationBbsrCode(e.target.value)}
                            className="w-full px-3 py-2 border border-blue-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="BBSR-Code (optional)"
                          />
                          <input
                            type="text"
                            value={classificationDescription}
                            onChange={(e) => setClassificationDescription(e.target.value)}
                            className="w-full px-3 py-2 border border-blue-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Beschreibung (optional)"
                          />
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => onCreateClassification(item.id, classificationName, classificationBbsrCode || undefined, classificationDescription || undefined)}
                          disabled={isResolving}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Klassifikation erstellen
                        </button>
                        <button
                          onClick={() => onResolve(item.id, 'reject', 'Code nicht benötigt')}
                          disabled={isResolving}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
                        >
                          <XCircle className="w-4 h-4" />
                          Ignorieren
                        </button>
                      </div>
                    </>
                  )}

                  {isMissingClassification && (
                    <>
                      <div className="bg-amber-50 border border-amber-200 rounded p-3">
                        <div className="text-sm font-medium text-amber-800 mb-2">
                          Klassifikation nicht im Import
                        </div>
                        <div className="text-sm text-amber-600">
                          Code <span className="font-mono font-bold">{item.entity_key}</span> existiert in der Datenbank,
                          wurde aber im aktuellen Import nicht verwendet. Möglicherweise veraltet oder nur für ältere Daten relevant.
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => onIgnoreMissing(item.id)}
                          disabled={isResolving}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          Beibehalten
                        </button>
                        <button
                          onClick={() => onDeleteClassification(item.id)}
                          disabled={isResolving}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                          <XCircle className="w-4 h-4" />
                          Löschen
                        </button>
                        <button
                          onClick={() => onResolve(item.id, 'defer', '')}
                          disabled={isResolving}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
                        >
                          <Clock className="w-4 h-4" />
                          Später prüfen
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : isEntityAssignmentItem ? (
                /* Entity assignment actions for unmatched entities */
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded p-3">
                    <div className="text-sm font-medium text-blue-800 mb-2 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Entität zuweisen
                    </div>
                    <div className="text-sm text-blue-600 mb-3">
                      Suche nach dem richtigen Kreis (Name oder AGS eingeben):
                    </div>

                    {/* Search Input */}
                    <div className="relative">
                      <div className="flex items-center">
                        <Search className="w-4 h-4 absolute left-3 text-gray-400" />
                        <input
                          type="text"
                          value={entitySearchQuery}
                          onChange={(e) => {
                            setEntitySearchQuery(e.target.value)
                            setSelectedEntity(null)
                          }}
                          className="w-full pl-9 pr-3 py-2 border border-blue-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="z.B. 'Schweinfurt' oder '09678'"
                        />
                        {isSearching && (
                          <Loader2 className="w-4 h-4 absolute right-3 text-gray-400 animate-spin" />
                        )}
                      </div>

                      {/* Search Results Dropdown */}
                      {searchResults && searchResults.length > 0 && !selectedEntity && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {searchResults.map((result) => (
                            <button
                              key={result.id}
                              onClick={() => {
                                setSelectedEntity(result)
                                setEntitySearchQuery(result.label)
                              }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center justify-between"
                            >
                              <span className="font-medium">{result.label}</span>
                              <span className="text-gray-500 font-mono text-xs">{result.key}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {searchResults && searchResults.length === 0 && entitySearchQuery.length >= 2 && !isSearching && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg p-3 text-sm text-gray-500">
                          Keine Ergebnisse gefunden
                        </div>
                      )}
                    </div>

                    {/* Selected Entity Display */}
                    {selectedEntity && (
                      <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded flex items-center justify-between">
                        <div>
                          <span className="font-medium text-green-800">{selectedEntity.label}</span>
                          <span className="text-green-600 ml-2 font-mono text-xs">AGS: {selectedEntity.key}</span>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedEntity(null)
                            setEntitySearchQuery('')
                          }}
                          className="text-green-600 hover:text-green-800"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Options */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={overwriteExisting}
                        onChange={(e) => setOverwriteExisting(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-700">Bestehenden Datensatz überschreiben (falls vorhanden)</span>
                    </label>
                    <input
                      type="text"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Notizen (optional)..."
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => selectedEntity && onAssignEntity(item.id, selectedEntity.id, notes, overwriteExisting)}
                      disabled={isResolving || !selectedEntity}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      <MapPin className="w-4 h-4" />
                      Zuweisen & Importieren
                    </button>
                    <button
                      onClick={() => onResolve(item.id, 'reject', notes)}
                      disabled={isResolving}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                      Verwerfen
                    </button>
                    <button
                      onClick={() => onResolve(item.id, 'defer', notes)}
                      disabled={isResolving}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
                    >
                      <Clock className="w-4 h-4" />
                      Zurückstellen
                    </button>
                  </div>
                </div>
              ) : (
                /* Standard actions for other items */
                <>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notizen (optional)
                    </label>
                    <input
                      type="text"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Anmerkungen zur Entscheidung..."
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => onResolve(item.id, 'approve', notes)}
                      disabled={isResolving}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Genehmigen
                    </button>
                    <button
                      onClick={() => onResolve(item.id, 'reject', notes)}
                      disabled={isResolving}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                      Ablehnen
                    </button>
                    <button
                      onClick={() => onResolve(item.id, 'defer', notes)}
                      disabled={isResolving}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
                    >
                      <Clock className="w-4 h-4" />
                      Zurückstellen
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Helper to group items by duplicate_group_key
 */
function groupItemsByDuplicate(items: QuarantineItem[]): {
  duplicateGroups: Map<string, QuarantineItem[]>
  standaloneItems: QuarantineItem[]
} {
  const duplicateGroups = new Map<string, QuarantineItem[]>()
  const standaloneItems: QuarantineItem[] = []

  for (const item of items) {
    const groupKey = item.raw_data?.duplicate_group_key as string | undefined
    if (groupKey) {
      if (!duplicateGroups.has(groupKey)) {
        duplicateGroups.set(groupKey, [])
      }
      duplicateGroups.get(groupKey)!.push(item)
    } else {
      standaloneItems.push(item)
    }
  }

  return { duplicateGroups, standaloneItems }
}

/**
 * Individual entity search for a duplicate item column
 */
function DuplicateItemColumn({
  item,
  index,
  total,
  selectedEntity,
  onSelectEntity,
  onReassign,
}: {
  item: QuarantineItem
  index: number
  total: number
  selectedEntity: EntitySearchResult | null
  onSelectEntity: (entity: EntitySearchResult | null) => void
  onReassign?: (itemId: number, entityId: number) => void
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [isCorrectingMode, setIsCorrectingMode] = useState(false)

  const { data: searchResults, isLoading: isSearching } = useQuery<EntitySearchResult[]>({
    queryKey: ['entity-search', searchQuery, item.entity_type, item.id],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2) return []
      const entityType = item.entity_type || 'county'
      const res = await fetch(
        `${API_BASE}/admin/quarantine/entities/search?q=${encodeURIComponent(searchQuery)}&entity_type=${entityType}&limit=10`
      )
      if (!res.ok) return []
      return res.json()
    },
    enabled: searchQuery.length >= 2 && !selectedEntity,
  })

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined || value === 'nan' || value === 'None') return '-'
    return String(value)
  }

  const rowData = item.raw_data?.row_data as Record<string, unknown> | undefined
  const isPending = item.status === 'pending'
  const isApproved = item.status === 'approved'

  // Get designation from row_data (Bezeichnung field)
  const designation = (rowData?.Bezeichnung || rowData?.['Stadt- bzw. Landkreis'] || rowData?.Name || item.entity_name || 'Unbekannt') as string

  // Get key data values to display (max 2)
  const dataValues: { label: string; value: string }[] = []
  if (rowData?.['Bevölkerung 01.01.2024']) {
    dataValues.push({ label: 'Bevölkerung', value: formatValue(rowData['Bevölkerung 01.01.2024']) })
  }
  if (rowData?.['Kaufkraftindex 2024 D=100']) {
    dataValues.push({ label: 'Kaufkraftindex', value: formatValue(rowData['Kaufkraftindex 2024 D=100']) })
  }

  return (
    <div className={cn(
      "border rounded-lg p-3",
      isPending ? "border-gray-200 bg-gray-50" : "border-green-200 bg-green-50"
    )}>
      {/* Item header */}
      <div className="flex items-center justify-between mb-3">
        <span className={cn(
          "px-2 py-1 rounded text-xs font-medium",
          isPending ? "bg-orange-100 text-orange-700" : "bg-green-100 text-green-700"
        )}>
          Eintrag {index + 1}/{total}
        </span>
        <StatusBadge status={item.status} />
      </div>

      {/* Entity info from CSV - Bezeichnung as title */}
      <div className="mb-3">
        <div className="font-medium text-gray-900">{designation}</div>
        <div className="text-sm text-gray-500">CSV-AGS: {item.entity_key}</div>
      </div>

      {/* Data preview - max 2 values */}
      {dataValues.length > 0 && (
        <div className="space-y-1 text-sm mb-3">
          {dataValues.slice(0, 2).map(({ label, value }) => (
            <div key={label} className="flex justify-between">
              <span className="text-gray-500 text-xs">{label}:</span>
              <span className="text-gray-900 font-mono text-xs">{value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Entity search - only for pending items */}
      {isPending && (
        <div className="border-t border-gray-200 pt-3 mt-3">
          <div className="text-xs font-medium text-gray-600 mb-2">Ziel-Entity:</div>

          {/* Selected entity display */}
          {selectedEntity ? (
            <div className="bg-green-100 border border-green-300 rounded-md p-2 mb-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-green-800">{selectedEntity.label}</div>
                  <div className="text-xs text-green-600">AGS: {selectedEntity.key}</div>
                </div>
                <button
                  onClick={() => onSelectEntity(null)}
                  className="text-green-600 hover:text-green-800"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Search input */}
              <div className="relative mb-2">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-7 pr-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Suchen..."
                />
                {isSearching && (
                  <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 animate-spin text-gray-400" />
                )}
              </div>

              {/* Search results dropdown */}
              {searchResults && searchResults.length > 0 && (
                <div className="border border-gray-200 rounded bg-white max-h-32 overflow-auto">
                  {searchResults.map((entity) => (
                    <button
                      key={entity.id}
                      onClick={() => {
                        onSelectEntity(entity)
                        setSearchQuery('')
                      }}
                      className="w-full px-2 py-1.5 text-left hover:bg-blue-50 flex items-center justify-between border-b border-gray-100 last:border-0 text-xs"
                    >
                      <span className="text-gray-900 truncate">{entity.label}</span>
                      <span className="text-gray-500 font-mono ml-1">{entity.key}</span>
                    </button>
                  ))}
                </div>
              )}

              {searchResults && searchResults.length === 0 && searchQuery.length >= 2 && (
                <div className="text-xs text-gray-500">Nichts gefunden</div>
              )}
            </>
          )}
        </div>
      )}

      {/* For approved items, show assignment info */}
      {isApproved && !isCorrectingMode && (
        <div className="border-t border-green-200 pt-3 mt-3">
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs font-medium text-green-700">Zugewiesen zu:</div>
            <button
              onClick={() => setIsCorrectingMode(true)}
              className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
            >
              Korrektur
            </button>
          </div>
          <div className="bg-green-100 border border-green-300 rounded-md p-2">
            <div className="text-sm font-medium text-green-800">
              {(item.raw_data?.assigned_entity_designation as string) ||
               (item.raw_data?.assigned_entity_name as string) ||
               item.resolution_notes?.replace('Entity zugewiesen: ', '→ ') ||
               'Unbekannt'}
            </div>
            {item.raw_data?.assigned_entity_key ? (
              <div className="text-xs text-green-600">
                AGS: {String(item.raw_data.assigned_entity_key)}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Correction mode for approved items */}
      {isApproved && isCorrectingMode && (
        <div className="border-t border-blue-200 pt-3 mt-3 bg-blue-50 -mx-3 -mb-3 p-3 rounded-b-lg">
          <div className="text-xs font-medium text-blue-700 mb-2">Neue Ziel-Entity auswählen:</div>
          <div className="relative mb-2">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-7 pr-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Neue Entity suchen..."
              autoFocus
            />
            {isSearching && (
              <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 animate-spin text-gray-400" />
            )}
          </div>
          {searchResults && searchResults.length > 0 && (
            <div className="border border-gray-200 rounded bg-white max-h-32 overflow-auto mb-2">
              {searchResults.map((entity) => (
                <button
                  key={entity.id}
                  onClick={() => {
                    if (onReassign) {
                      onReassign(item.id, entity.id)
                    }
                    setSearchQuery('')
                    setIsCorrectingMode(false)
                  }}
                  className="w-full px-2 py-1.5 text-left hover:bg-blue-50 flex items-center justify-between border-b border-gray-100 last:border-0 text-xs"
                >
                  <span className="text-gray-900 truncate">{entity.label}</span>
                  <span className="text-gray-500 font-mono ml-1">{entity.key}</span>
                </button>
              ))}
            </div>
          )}
          {searchResults && searchResults.length === 0 && searchQuery.length >= 2 && (
            <div className="text-xs text-gray-500 mb-2">Nichts gefunden</div>
          )}
          <button
            onClick={() => {
              setIsCorrectingMode(false)
              setSearchQuery('')
            }}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Abbrechen
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Side-by-side view for duplicate items with separate entity search per column
 */
function DuplicateGroupView({
  items,
  onResolve,
  onAssignEntity,
  onReassignEntity,
  isResolving,
}: {
  items: QuarantineItem[]
  onResolve: (id: number, action: string, notes?: string) => void
  onAssignEntity: (id: number, entityId: number, notes?: string, overwrite?: boolean) => void
  onReassignEntity: (id: number, entityId: number) => void
  isResolving: boolean
}) {
  // State for selected entities - one per item
  const [selectedEntities, setSelectedEntities] = useState<Record<number, EntitySearchResult | null>>({})
  const [notes, setNotes] = useState('')

  // Get matched entity info from first item
  const matchedEntityId = items[0]?.raw_data?.matched_entity_id as number | undefined

  // Check if all pending items have a selected entity
  const pendingItems = items.filter(item => item.status === 'pending')
  const allPendingHaveSelection = pendingItems.every(item => selectedEntities[item.id])
  const anyPendingHasSelection = pendingItems.some(item => selectedEntities[item.id])

  // Extract base name from entity label (e.g., "Coburg" from "Coburg, kreisfreie Stadt")
  const extractBaseName = (label: string): string => {
    // Remove common suffixes and get the base name
    return label.split(',')[0].trim()
  }

  // Intelligent suggestion: when one entity is selected, search for similar for others
  const handleEntitySelect = async (itemId: number, entity: EntitySearchResult | null) => {
    setSelectedEntities(prev => ({ ...prev, [itemId]: entity }))

    // If entity selected and there are other pending items without selection
    if (entity) {
      const baseName = extractBaseName(entity.label)
      const otherPendingItems = pendingItems.filter(i => i.id !== itemId && !selectedEntities[i.id])

      if (otherPendingItems.length > 0) {
        // Search for entities with the same base name
        try {
          const entityType = items[0]?.entity_type || 'county'
          const res = await fetch(
            `${API_BASE}/admin/quarantine/entities/search?q=${encodeURIComponent(baseName)}&entity_type=${entityType}&limit=10`
          )
          if (res.ok) {
            const results: EntitySearchResult[] = await res.json()
            // Find entities with same base name but different from selected
            const suggestions = results.filter(r =>
              r.id !== entity.id &&
              extractBaseName(r.label).toLowerCase() === baseName.toLowerCase()
            )

            // Auto-select suggestions for other items
            if (suggestions.length > 0) {
              const newSelections: Record<number, EntitySearchResult> = {}
              otherPendingItems.forEach((item, idx) => {
                if (suggestions[idx]) {
                  newSelections[item.id] = suggestions[idx]
                }
              })
              if (Object.keys(newSelections).length > 0) {
                setSelectedEntities(prev => ({ ...prev, ...newSelections }))
              }
            }
          }
        } catch {
          // Silently fail - manual selection still works
        }
      }
    }
  }

  // Handle assigning all selected entities
  const handleAssignAll = async () => {
    for (const item of pendingItems) {
      const entity = selectedEntities[item.id]
      if (entity) {
        onAssignEntity(item.id, entity.id, notes, true)
      }
    }
  }

  return (
    <div className="bg-white border border-orange-300 rounded-lg mb-4 overflow-hidden">
      {/* Header */}
      <div className="bg-orange-50 px-4 py-3 border-b border-orange-200">
        <div className="flex items-center gap-2 text-orange-800">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-semibold">
            Duplikat: {items.length} CSV-Zeilen mit AGS {items[0]?.entity_key}
          </span>
        </div>
        <div className="text-sm text-orange-600 mt-1">
          Beide Zeilen matchen zur gleichen DB-Entity (ID: {matchedEntityId}).
          Suche jeweils die korrekte Ziel-Entity und weise beide zu.
        </div>
      </div>

      {/* Side-by-side columns */}
      <div className="p-4">
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${items.length}, 1fr)` }}>
          {items.map((item, idx) => (
            <DuplicateItemColumn
              key={item.id}
              item={item}
              index={idx}
              total={items.length}
              selectedEntity={selectedEntities[item.id] || null}
              onSelectEntity={(entity) => handleEntitySelect(item.id, entity)}
              onReassign={onReassignEntity}
            />
          ))}
        </div>

        {/* Action buttons - only show if there are pending items */}
        {pendingItems.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-3">
              {/* Notes input */}
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Notizen (optional)..."
              />

              {/* Assign all button */}
              <button
                onClick={handleAssignAll}
                disabled={isResolving || !allPendingHaveSelection}
                className={cn(
                  "inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium",
                  allPendingHaveSelection
                    ? "bg-green-600 text-white hover:bg-green-700"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                )}
              >
                <CheckCircle2 className="w-4 h-4" />
                Alle zuweisen ({pendingItems.filter(i => selectedEntities[i.id]).length}/{pendingItems.length})
              </button>

              {/* Reject all button */}
              <button
                onClick={() => pendingItems.forEach(item => onResolve(item.id, 'reject', notes))}
                disabled={isResolving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors text-sm"
              >
                <XCircle className="w-4 h-4" />
                Alle ablehnen
              </button>
            </div>

            {!allPendingHaveSelection && anyPendingHasSelection && (
              <div className="text-xs text-blue-600 mt-2">
                Bitte für alle Einträge eine Ziel-Entity auswählen
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export function QuarantinePage() {
  const [statusFilter, setStatusFilter] = useState<string>('pending')
  const [typeFilter, setTypeFilter] = useState<string>('')
  const queryClient = useQueryClient()

  // Fetch items
  const { data: itemsData, isLoading, error, refetch } = useQuery<QuarantineListResponse>({
    queryKey: ['quarantine-items', statusFilter, typeFilter],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (statusFilter) params.set('status', statusFilter)
      if (typeFilter) params.set('quarantine_type', typeFilter)
      params.set('limit', '100')
      const res = await fetch(`${API_BASE}/admin/quarantine/items?${params}`)
      if (!res.ok) throw new Error('Failed to fetch quarantine items')
      return res.json()
    },
  })

  // Fetch stats
  const { data: stats } = useQuery<QuarantineStats>({
    queryKey: ['quarantine-stats'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/admin/quarantine/stats`)
      if (!res.ok) throw new Error('Failed to fetch stats')
      return res.json()
    },
  })

  // Resolve mutation
  const resolveMutation = useMutation({
    mutationFn: async ({ id, action, notes }: { id: number; action: string; notes?: string }) => {
      const res = await fetch(`${API_BASE}/admin/quarantine/items/${id}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, notes }),
      })
      if (!res.ok) throw new Error('Failed to resolve item')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quarantine-items'] })
      queryClient.invalidateQueries({ queryKey: ['quarantine-stats'] })
    },
  })

  // Create classification mutation
  const createClassificationMutation = useMutation({
    mutationFn: async ({ id, name, bbsr_code, description }: { id: number; name: string; bbsr_code?: string; description?: string }) => {
      const res = await fetch(`${API_BASE}/admin/quarantine/items/${id}/create-classification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, bbsr_code, description }),
      })
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.detail || 'Failed to create classification')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quarantine-items'] })
      queryClient.invalidateQueries({ queryKey: ['quarantine-stats'] })
    },
  })

  // Ignore missing classification mutation
  const ignoreMissingMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${API_BASE}/admin/quarantine/items/${id}/ignore-missing`, {
        method: 'POST',
      })
      if (!res.ok) throw new Error('Failed to ignore missing classification')
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quarantine-items'] })
      queryClient.invalidateQueries({ queryKey: ['quarantine-stats'] })
    },
  })

  // Delete classification mutation
  const deleteClassificationMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${API_BASE}/admin/quarantine/items/${id}/delete-classification`, {
        method: 'DELETE',
      })
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.detail || 'Failed to delete classification')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quarantine-items'] })
      queryClient.invalidateQueries({ queryKey: ['quarantine-stats'] })
    },
  })

  // Assign entity mutation
  const assignEntityMutation = useMutation({
    mutationFn: async ({ id, entityId, notes, overwrite }: { id: number; entityId: number; notes?: string; overwrite?: boolean }) => {
      const res = await fetch(`${API_BASE}/admin/quarantine/items/${id}/assign-entity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entity_id: entityId, notes, overwrite: overwrite || false }),
      })
      if (!res.ok) {
        const error = await res.json()
        // Include status code in error for conflict handling
        const err = new Error(error.detail || 'Failed to assign entity') as Error & { status?: number }
        err.status = res.status
        throw err
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quarantine-items'] })
      queryClient.invalidateQueries({ queryKey: ['quarantine-stats'] })
    },
  })

  const handleResolve = (id: number, action: string, notes?: string) => {
    resolveMutation.mutate({ id, action, notes })
  }

  const handleAssignEntity = (id: number, entityId: number, notes?: string, overwrite?: boolean) => {
    assignEntityMutation.mutate({ id, entityId, notes, overwrite })
  }

  // Reassign entity mutation (for correcting already-approved items)
  const reassignEntityMutation = useMutation({
    mutationFn: async ({ id, newEntityId }: { id: number; newEntityId: number }) => {
      const res = await fetch(`${API_BASE}/admin/quarantine/items/${id}/reassign-entity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_entity_id: newEntityId }),
      })
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.detail || 'Failed to reassign entity')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quarantine-items'] })
      queryClient.invalidateQueries({ queryKey: ['quarantine-stats'] })
    },
  })

  const handleReassignEntity = (id: number, newEntityId: number) => {
    reassignEntityMutation.mutate({ id, newEntityId })
  }

  const handleCreateClassification = (id: number, name: string, bbsr_code?: string, description?: string) => {
    createClassificationMutation.mutate({ id, name, bbsr_code, description })
  }

  const handleIgnoreMissing = (id: number) => {
    ignoreMissingMutation.mutate(id)
  }

  const handleDeleteClassification = (id: number) => {
    if (confirm('Klassifikation wirklich löschen? Dies kann nicht rückgängig gemacht werden.')) {
      deleteClassificationMutation.mutate(id)
    }
  }

  const isAnyMutationPending =
    resolveMutation.isPending ||
    createClassificationMutation.isPending ||
    ignoreMissingMutation.isPending ||
    deleteClassificationMutation.isPending ||
    assignEntityMutation.isPending ||
    reassignEntityMutation.isPending

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quarantäne</h1>
          <p className="text-gray-500 mt-1">Unzugeordnete Daten prüfen und bearbeiten</p>
        </div>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Aktualisieren
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => setStatusFilter('pending')}
            className={cn(
              'p-4 rounded-lg border text-left transition-all',
              statusFilter === 'pending'
                ? 'border-yellow-500 bg-yellow-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            )}
          >
            <div className="flex items-center gap-2 text-sm text-yellow-600">
              <Clock className="w-4 h-4" />
              Ausstehend
            </div>
            <div className="text-2xl font-bold mt-1">{stats.pending_items}</div>
          </button>
          <button
            onClick={() => setStatusFilter('approved')}
            className={cn(
              'p-4 rounded-lg border text-left transition-all',
              statusFilter === 'approved'
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            )}
          >
            <div className="flex items-center gap-2 text-sm text-green-600">
              <CheckCircle2 className="w-4 h-4" />
              Genehmigt
            </div>
            <div className="text-2xl font-bold mt-1">{stats.approved_items}</div>
          </button>
          <button
            onClick={() => setStatusFilter('rejected')}
            className={cn(
              'p-4 rounded-lg border text-left transition-all',
              statusFilter === 'rejected'
                ? 'border-red-500 bg-red-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            )}
          >
            <div className="flex items-center gap-2 text-sm text-red-600">
              <XCircle className="w-4 h-4" />
              Abgelehnt
            </div>
            <div className="text-2xl font-bold mt-1">{stats.rejected_items}</div>
          </button>
          <button
            onClick={() => setStatusFilter('')}
            className={cn(
              'p-4 rounded-lg border text-left transition-all',
              statusFilter === ''
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            )}
          >
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Package className="w-4 h-4" />
              Gesamt
            </div>
            <div className="text-2xl font-bold mt-1">{stats.total_items}</div>
          </button>
        </div>
      )}

      {/* Type Filter */}
      {stats && Object.keys(stats.by_type).length > 0 && (
        <div className="flex flex-wrap gap-2">
          <span className="text-sm text-gray-500 py-1">Filter nach Typ:</span>
          <button
            onClick={() => setTypeFilter('')}
            className={cn(
              'px-3 py-1 text-sm rounded-full transition-colors',
              typeFilter === ''
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            Alle
          </button>
          {Object.entries(stats.by_type).map(([type, count]) => (
            <button
              key={type}
              onClick={() => setTypeFilter(typeFilter === type ? '' : type)}
              className={cn(
                'px-3 py-1 text-sm rounded-full transition-colors',
                typeFilter === type
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              {typeLabels[type] || type} ({count})
            </button>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="w-5 h-5" />
            <span>Fehler beim Laden der Quarantäne-Items</span>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      )}

      {/* Items List */}
      {itemsData && (
        <div>
          <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
            <span>{itemsData.total} Items gefunden ({itemsData.pending} ausstehend)</span>
          </div>
          {itemsData.items.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
              <FileQuestion className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <div className="text-gray-500">Keine Quarantäne-Items gefunden</div>
              {statusFilter && (
                <button
                  onClick={() => setStatusFilter('')}
                  className="text-blue-600 hover:underline text-sm mt-2"
                >
                  Filter zurücksetzen
                </button>
              )}
            </div>
          ) : (
            <div>
              {/* Group items by duplicate_group_key and render appropriately */}
              {(() => {
                const { duplicateGroups, standaloneItems } = groupItemsByDuplicate(itemsData.items)
                return (
                  <>
                    {/* Render duplicate groups first */}
                    {Array.from(duplicateGroups.entries()).map(([groupKey, groupItems]) => (
                      <DuplicateGroupView
                        key={groupKey}
                        items={groupItems}
                        onResolve={handleResolve}
                        onAssignEntity={handleAssignEntity}
                        onReassignEntity={handleReassignEntity}
                        isResolving={isAnyMutationPending}
                      />
                    ))}

                    {/* Render standalone items */}
                    {standaloneItems.map((item) => (
                      <QuarantineItemRow
                        key={item.id}
                        item={item}
                        onResolve={handleResolve}
                        onCreateClassification={handleCreateClassification}
                        onIgnoreMissing={handleIgnoreMissing}
                        onDeleteClassification={handleDeleteClassification}
                        onAssignEntity={handleAssignEntity}
                        isResolving={isAnyMutationPending}
                      />
                    ))}
                  </>
                )
              })()}
            </div>
          )}
        </div>
      )}

      {/* Recent Batches */}
      {stats && stats.recent_batches.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Aktuelle Batches</h2>
          <div className="space-y-2">
            {stats.recent_batches.map((batch) => (
              <div
                key={batch.batch_id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div>
                  <div className="font-medium text-gray-900">{batch.name}</div>
                  <div className="text-sm text-gray-500">
                    {batch.pending_items} ausstehend von {batch.total_items}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-green-600">{batch.approved_items} genehmigt</span>
                  <span className="text-red-600">{batch.rejected_items} abgelehnt</span>
                  {batch.is_complete ? (
                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">Abgeschlossen</span>
                  ) : (
                    <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs">In Bearbeitung</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
